import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Llm } from '../../../api/Llm';
import { RagListDTO } from '../../../models/LLM';
import { useToasts } from '../../../contexts/toasts';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { TailSpin } from 'react-loader-spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import AppSearch from '../../../components/common/AppSearch';
import { debounce } from 'lodash';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { accessLevelOptions, formatDate } from '../../../utils';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import UserItem from '../../../components/common/UserItem';
import { KrakendCollection } from '../../../models/generics';


export default function RagsList() {
  const navigate = useNavigate();
  const [rags, setRags] = useState<RagListDTO[]>();
  const [sortAsc, setSortAsc] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState<number>();
  const [rag, setRag] = useState<RagListDTO>();
  const [validated, setValidated] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const filteredRags = useMemo(() => rags?.filter(r => r.name.toLowerCase().includes(searchText.toLowerCase())), [rags, searchText]);

  const initRags = useCallback(() => {
    const params = new URLSearchParams();
    params.set("sort", "id");
    params.set("desc", `${!sortAsc}`);

    Llm.getRags(params)
      .then(
        ({ data }) => setRags((data as KrakendCollection<RagListDTO>).collection),
        (err) => {
          setRags([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rags-error', message, `Rags Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast, sortAsc]);

  const reset = useCallback(() => {
    setRags(undefined);
    initRags();
  }, [initRags]);

  useEffect(() => reset(), [reset, sortAsc]);

  const handleClose = useCallback(() => {
    setShowEditModal(false)
    setShowDeleteModal(false);
    setRag(undefined);
    setValidated(false);
    setEditing(undefined);
  }, []);

  const toggleSort = useCallback(() => setSortAsc(v => !v), []);
  const openCreateModal = useCallback(() => setShowEditModal(true), []);
  const navigateToChat = useCallback((rag: RagListDTO) => () => navigate(`/llm/rags/${rag.id}`), [navigate]);

  const handleRagEdit = useCallback((rag: RagListDTO, index: number) => () => {
    setEditing(index);
    setRag(rag);
    setShowEditModal(true);
  }, []);

  const handleRagDelete = useCallback((rag: RagListDTO) => () => {
    setRag(rag);
    setShowDeleteModal(true);
  }, []);

  const handleSubmit = useCallback((evt: FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();

    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader();

      const promise = editing !== undefined
        ? Llm.editRag(rag!)
        : Llm.createRag(rag!);

      promise.then(
        ({ data }) => {
          hideLoader();
          showToast('rag-success', `Rag '${rag!.name}' ${rag!.id ? 'updated' : 'created'} successfully`, `Rags ${rag!.id ? 'Updated' : 'Created'}`, 'success');
          handleClose();
          navigate(`/llm/rags/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rags-error', message, `Rags Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    }
  }, [rag, navigate, editing, handleClose, showLoader, hideLoader, showToast]);

  const handleFieldChange = useCallback((evt: any) => {
    if (Array.isArray(evt)) {
      setRag(r => ({
        ...r!,
        tags: evt,
      }));
    } else if (typeof evt === 'object') {
      const field = evt.target.name;
      const value = evt.target.value;

      setRag(r => ({
        ...r!,
        [field]: value,
      }));
    } else {
      setRag(r => ({
        ...r!,
        accessLevel: evt,
      }));
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => setSearchText(value), []);
  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const deleteRag = useCallback(() => {
    showLoader();

    Llm.deleteRag(rag!.id)
      .then(
        ({ data }) => {
          hideLoader();
          handleClose();
          reset();
          showToast('rag-success', 'Rag deleted successfully', 'Rag Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-error', message, `Rags Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, rag, reset, showToast]);

  const renderRags = useCallback(() => {
    return (
      <Row>
        {filteredRags?.map((rag, index) => (
          <Col key={rag.id} md={3} className="px-2">
            <Card className="shadow-sm mb-3">
              <div className="d-flex p-1">
                <Badge className="shadow w-auto">{rag.accessLevel}</Badge>
              </div>
              <Card.Body className="py-2">
                <Card.Title className="mb-1">{rag.name}</Card.Title>
                <p className="line-clamp-2 text-secondary mb-2">{rag.description}</p>

                <div className="d-flex mb-3">
                  {rag.tags.length === 0 && <Badge className="bg-secondary" pill={true}>0 tags</Badge>}
                  {rag.tags.map((tag, index) => (
                    <Badge key={index} className="me-1" pill={true}>
                      <FontAwesomeIcon icon={solid('tag')} />
                      <span className="ms-2">{tag}</span>
                    </Badge>
                  ))}
                </div>

                <Row className="justify-content-center">
                  <button className="circle-button dark" onClick={navigateToChat(rag)}>
                    <FontAwesomeIcon icon={solid('comments')} />
                  </button>
                  <button className="circle-button dark ms-2" onClick={handleRagEdit(rag, index)}>
                    <FontAwesomeIcon icon={solid('pen')} />
                  </button>
                  <button className="circle-button dark ms-2" onClick={handleRagDelete(rag)}>
                    <FontAwesomeIcon icon={solid('trash')} />
                  </button>
                </Row>
              </Card.Body>
              <UserItem className="border rounded p-1 m-2" user={rag.owner!} organization={rag.organization!} />
              <small className="ms-1 font-sm text-muted">
                <b>Created:</b>
                <span className="ms-1">{formatDate(rag.createDateTime)}</span>
              </small>
            </Card>
          </Col>
        ))}
      </Row>
    )
  }, [filteredRags, navigateToChat, handleRagEdit, handleRagDelete]);

  const renderAsyncContent = useCallback(() => {
    if (!filteredRags) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    return filteredRags.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No rags found.</span>
        </Alert>
      )
      : renderRags();
  }, [filteredRags, renderRags]);

  return (
    <Container className="px-0" fluid>
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">Rags List</h4>
        </Col>

        <Col>
          <AppSearch placeholder="Search rag..." onChange={debounceSearchChange} />
        </Col>

        <Col xs={6} md={4} className="text-end">
          <Button size="sm" variant="light" className="border me-2" onClick={toggleSort}>
            <FontAwesomeIcon icon={sortAsc ? solid('sort-numeric-up-alt') : solid('sort-numeric-down-alt')} />
            <span className="d-none d-sm-inline ms-2">Sort: <b>{sortAsc ? 'ascending' : 'descending'}</b></span>
          </Button>

          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create Rag</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? 'Edit Rag' : 'New Rag'}</Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="rag-name">
              <Form.Label>Name</Form.Label>
              <Form.Control name="name" placeholder="Rag Name" size="sm" value={rag?.name} onChange={handleFieldChange} required />
              <Form.Control.Feedback type="invalid">Rag name is required.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="rag-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" placeholder="Rag Description" size="sm" value={rag?.description} onChange={handleFieldChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Privacy Level</Form.Label>
              <Select
                placeholder="Select privacy level"
                value={rag?.accessLevel}
                allowClear
                onChange={handleFieldChange}
                className="w-100"
              >
                {accessLevelOptions.map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <FontAwesomeIcon icon={option.icon} fixedWidth />
                    <span className="ms-2">{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Select mode="tags" className="w-100" placeholder="Rag Tags" value={rag?.tags} onChange={handleFieldChange} />
            </Form.Group>

          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
            <Button type="submit" variant="success" size="sm">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Save</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Rag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete rag <b>{rag?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteRag}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}